import isEmpty from "lodash/isEmpty";
import { TemplateKey } from "PFApp/constants/templates";
import { getFieldTitle } from "PFApp/helpers/activity_form/multi_roles_form/get_field_title";
import { useTemplate } from "PFApp/hooks";
import { MultiActivityForm } from "PFApp/types/activity_form/activity_form_schema";
import { Alert } from "PFComponents/alert";
import { ListItem, UnorderedList } from "PFComponents/ul";
import { Typography } from "PFCore/components/typography";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useColumnsNames } from "../../hooks/use_column_names";
import { countErrors, groupErrorsByRoleIndex, hasMandatoryErrors } from "./validation_alert.utils";

export const ValidationAlert = ({ className }: { className?: string }) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.validationAlert" });
  const roleTemplate = useTemplate(TemplateKey.Role);
  const { customTypes } = useCustomTypes();
  const columnNames = useColumnsNames();
  const {
    formState: { errors },
    getValues
  } = useFormContext<MultiActivityForm>();

  if (isEmpty(errors)) {
    return null;
  }

  const displayMandatoryError = hasMandatoryErrors(errors.subtemplates);
  const errorCount = countErrors(errors.subtemplates);
  const groupedErrors = groupErrorsByRoleIndex({ errors: errors.subtemplates, formValues: getValues() });

  const title = displayMandatoryError
    ? t("mandatoryFieldsMissing")
    : !errorCount
      ? t("genericError")
      : t("errorsFound", { count: errorCount });

  return (
    <Alert kind="error" title={title} className={className} qaId="MultiActivityForm.ValidationAlert">
      {!displayMandatoryError && !isEmpty(groupedErrors) && (
        <UnorderedList>
          {Object.entries(groupedErrors).map(([roleIndex, roleErrors]) => (
            <ListItem key={roleIndex}>
              <Typography variant="bodyRegular">{t("role", { index: Number(roleIndex) + 1 })}</Typography>
              <UnorderedList>
                {roleErrors.map((error, idx) => (
                  <ListItem key={idx}>
                    <Typography variant="bodyRegular">
                      {getFieldTitle(error.name, roleTemplate!, customTypes, columnNames)} - {error.message}
                    </Typography>
                  </ListItem>
                ))}
              </UnorderedList>
            </ListItem>
          ))}
        </UnorderedList>
      )}
    </Alert>
  );
};
